import { computed, ref, watch } from 'vue';

import {
  documentSendApi,
  type DocumentSendRecipientReport,
  type DocumentSendRecipientType,
} from '@/entities/DocumentSend';
import {
  buildProtheusPartyKey,
  protheusApi,
  resolveProtheusRecipientEmail,
  type ProtheusClient,
  type ProtheusSupplier,
} from '@/entities/Protheus';
import { useCustomToast } from '@/shared/lib';

export interface RecipientOption {
  id: string;
  code: string;
  store: string;
  name: string;
  email: string | null;
}

function normalizeRecipientEmail(
  recipient: ProtheusClient | ProtheusSupplier,
  recipientType: DocumentSendRecipientType
): string | null {
  const email = resolveProtheusRecipientEmail(recipient, recipientType).trim();
  return email !== '' ? email : null;
}

export function useDocumentSendRecipientReport() {
  const toast = useCustomToast();

  const selectedRecipientType = ref<DocumentSendRecipientType | ''>('');
  const searchTerm = ref('');
  const selectedRecipient = ref<RecipientOption | null>(null);
  const report = ref<DocumentSendRecipientReport | null>(null);
  const loadingReport = ref(false);
  const isLoadingRecipients = ref(false);
  const clientRecipients = ref<RecipientOption[]>([]);
  const supplierRecipients = ref<RecipientOption[]>([]);

  const recipientOptions = computed(() => {
    if (selectedRecipientType.value === 'CLIENT') {
      return clientRecipients.value;
    }

    if (selectedRecipientType.value === 'SUPPLIER') {
      return supplierRecipients.value;
    }

    return [];
  });

  const filteredRecipients = computed(() => {
    const normalizedTerm = searchTerm.value.trim().toLocaleLowerCase('pt-BR');

    if (normalizedTerm === '') {
      return [];
    }

    return recipientOptions.value.filter((recipient) =>
      recipient.name.toLocaleLowerCase('pt-BR').includes(normalizedTerm)
    );
  });

  const canGenerateReport = computed(
    () => selectedRecipient.value !== null && !loadingReport.value
  );

  function resetState(): void {
    selectedRecipientType.value = '';
    searchTerm.value = '';
    selectedRecipient.value = null;
    report.value = null;
  }

  function setRecipientType(value: DocumentSendRecipientType | ''): void {
    selectedRecipientType.value = value;
  }

  function updateSearchTerm(value: string): void {
    searchTerm.value = value;

    if (selectedRecipient.value?.name !== value) {
      selectedRecipient.value = null;
      report.value = null;
    }
  }

  function selectRecipient(recipient: RecipientOption): void {
    selectedRecipient.value = recipient;
    searchTerm.value = recipient.name;
    report.value = null;
  }

  function clearSelectedRecipient(): void {
    selectedRecipient.value = null;
    searchTerm.value = '';
    report.value = null;
  }

  async function loadRecipients(recipientType: DocumentSendRecipientType): Promise<void> {
    const hasCachedData =
      (recipientType === 'CLIENT' && clientRecipients.value.length > 0) ||
      (recipientType === 'SUPPLIER' && supplierRecipients.value.length > 0);

    if (hasCachedData) {
      return;
    }

    isLoadingRecipients.value = true;

    try {
      const recipients =
        recipientType === 'CLIENT'
          ? await protheusApi.getClients()
          : await protheusApi.getSuppliers();

      const options = recipients.map((recipient) => ({
        id: buildProtheusPartyKey(recipient),
        code: recipient.code,
        store: recipient.store,
        name: recipient.name,
        email: normalizeRecipientEmail(recipient, recipientType),
      }));

      if (recipientType === 'CLIENT') {
        clientRecipients.value = options;
        return;
      }

      supplierRecipients.value = options;
    } catch {
      toast.showErrorToast({
        title:
          recipientType === 'CLIENT'
            ? 'Erro ao carregar clientes'
            : 'Erro ao carregar fornecedores',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      isLoadingRecipients.value = false;
    }
  }

  async function generateReport(): Promise<void> {
    if (!selectedRecipient.value || !selectedRecipientType.value) {
      return;
    }

    loadingReport.value = true;

    try {
      const response = await documentSendApi.getDocumentSendReportByRecipient({
        recipientType: selectedRecipientType.value,
        protheusCode: selectedRecipient.value.code,
        protheusStore: selectedRecipient.value.store,
      });
      report.value = response.data;
    } catch {
      toast.showErrorToast({
        title: 'Erro ao gerar lista de envio',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      loadingReport.value = false;
    }
  }

  watch(selectedRecipientType, (next, previous) => {
    if (next === previous) {
      return;
    }

    searchTerm.value = '';
    selectedRecipient.value = null;
    report.value = null;

    if (next) {
      void loadRecipients(next);
    }
  });

  return {
    canGenerateReport,
    filteredRecipients,
    generateReport,
    isLoadingRecipients,
    loadingReport,
    report,
    clearSelectedRecipient,
    resetState,
    searchTerm,
    selectRecipient,
    selectedRecipient,
    selectedRecipientType,
    setRecipientType,
    updateSearchTerm,
  };
}
